import { useEffect, useState } from "react";
import {
  disableWebPush,
  enableWebPush,
  getWebPushStatus,
  type WebPushStatus,
} from "../lib/webpush";

// Settings card for browser-push opt-in. Only renders on browsers
// that support Web Push — iOS Capacitor builds skip it (the
// IOSDesignPanel etc. cover that path) and ancient browsers that
// lack PushManager just hide the toggle.
//
// Permission is per-origin per-browser, granted by the user via
// the system prompt. The button label adapts to whichever state
// the browser reports:
//   - default → "Enable browser notifications"   (will prompt)
//   - granted → "Disable browser notifications"
//   - blocked → static note pointing them at site settings
//   - unsupported → don't render at all
export function WebPushPanel() {
  const [status, setStatus] = useState<WebPushStatus | "loading">("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getWebPushStatus().then((s) => { if (!cancelled) setStatus(s); });
    return () => { cancelled = true; };
  }, []);

  if (status === "loading") return null;
  if (status === "unsupported") return null;

  async function turnOn() {
    setBusy(true);
    try {
      const next = await enableWebPush();
      setStatus(next);
    } finally {
      setBusy(false);
    }
  }
  async function turnOff() {
    setBusy(true);
    try {
      await disableWebPush();
      setStatus("default");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">
          Browser notifications
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Get a desktop banner when a new notification lands, even when
          this tab isn't open. Per-browser, per-device — turning it on
          here doesn't affect your other browsers or your phone.
        </p>
      </div>

      {status === "granted" && (
        <div className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-3">
          <div>
            <div className="text-sm font-semibold text-emerald-700">
              ✓ Enabled on this browser
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              You'll receive a system notification when an alert lands.
            </p>
          </div>
          <button
            type="button"
            onClick={turnOff}
            disabled={busy}
            className="shrink-0 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? "…" : "Disable"}
          </button>
        </div>
      )}

      {status === "default" && (
        <div className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-3">
          <div>
            <div className="text-sm font-semibold text-slate-800">
              Off
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              Your browser will prompt for permission when you tap Enable.
            </p>
          </div>
          <button
            type="button"
            onClick={turnOn}
            disabled={busy}
            className="shrink-0 text-xs font-semibold text-white bg-sky-600 hover:bg-sky-700 rounded px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? "…" : "Enable"}
          </button>
        </div>
      )}

      {status === "blocked" && (
        <div className="border border-amber-200 bg-amber-50 rounded-md px-3 py-3 text-xs text-amber-800">
          Notifications are blocked for this site in your browser settings.
          To re-enable, open this site's permissions (click the lock icon
          in the address bar) and switch Notifications to Allow, then
          refresh the page.
        </div>
      )}
    </section>
  );
}
